import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { FishGroupMetadataChange } from '@/domain/fishGroups';
import { formatNumberMaxDecimalsByLocale, type LocaleInput } from '@/utils/localeFormatters';

import styles from '../FishGroupDetailModal.module.scss';

interface Props {
    changes: FishGroupMetadataChange[];
    operatorName: string | null;
    operatorId: number;
}

const EMPTY = '—';

/**
 * Renders the field-level diff of a `metadata_updated` event.
 *
 * Used instead of the raw JSON dump the other event types fall back to: a
 * correction's whole value is "which field went from what to what", which a
 * stringified payload buries.
 */
export default function MetadataChangeDetails(props: Props) {
    const { changes, operatorName, operatorId } = props;
    const { t, i18n } = useTranslation('fishGroups');

    return (
        <div className={styles.metadataChange}>
            <p className={styles.metadataChangeOperator}>
                {t('detail.events.metadataChange.operator')}{' '}
                {/* Older events predate the name snapshot, so fall back to the id. */}
                <span className={styles.metadataChangeOperatorName}>
                    {operatorName ?? operatorId}
                </span>
            </p>
            <div className={styles.metadataChangeGrid}>
                <span className={styles.metadataChangeHeadCell}>
                    {t('detail.events.metadataChange.field')}
                </span>
                <span className={styles.metadataChangeHeadCell}>
                    {t('detail.events.metadataChange.from')}
                </span>
                <span className={styles.metadataChangeHeadCell}>
                    {t('detail.events.metadataChange.to')}
                </span>
                {changes.map((change) => (
                    <Fragment key={change.field}>
                        <span className={styles.metadataChangeFieldCell}>
                            {t(`detail.events.fields.${change.field}`, {
                                defaultValue: change.field,
                            })}
                        </span>
                        <span>{formatValue(change.from, i18n.language)}</span>
                        <span>{formatValue(change.to, i18n.language)}</span>
                    </Fragment>
                ))}
            </div>
        </div>
    );
}

function formatValue(value: string | number | null, locale: LocaleInput) {
    if (value === null || value === '') return <span className={styles.placeholder}>{EMPTY}</span>;
    return typeof value === 'number' ? formatNumberMaxDecimalsByLocale(value, locale) : value;
}
